import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { Dispatch } from 'redux';

import { WithStyles } from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/core';
import grey from '@material-ui/core/colors/grey';
import Paper from '@material-ui/core/Paper';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';

import { StaticContext } from 'react-router';
import { IRootState } from 'src/reducers';
import {
	ISkillMapMaterialFile,
	setSkillMapDetail,
} from '../actions/skillMapDetail';
import i18n from '../resources/i18n';
import ScenarioFactory from '../scenario/SkillMapDetailScenarioFactory';
import Config from '../site.config.json';

const styles = (theme: Theme) =>
	createStyles({
		cellItemName: {
			backgroundColor: grey[300],
			minWidth: '160px',
			textAlign: 'right',
			width: '20%',
		},
		cellItemValue: {
			textAlign: 'left',
			width: '80%',
		},
		downloadButton: {
			cursor: 'pointer',
			marginLeft: 20,
			verticalAlign: 'middle',
		},
		fileLink: {
			marginLeft: 20,
		},
		flex: {
			flex: 1,
		},
		link: {
			color: 'rgb(0,136,204)',
			cursor: 'pointer',
			fontWeight: 'bold',
		},
		paper: {
			overflowX: 'auto',
		},
		root: {
			flexGrow: 1,
			padding: 18,
		},
		table: {
			minWidth: '700px',
		},
		wrapDiv: {
			margin: '10px',
		},
	});

export interface ISkillMapDetailProps {
	skillMapId: string; // スキルマップID
}

interface IRouteParams {
	skill_map_id: string;
}

// tslint:disable-next-line:no-empty-interface
interface IRouteState {}

interface IState {
	skillMapId: string; // スキルマップID
}

/**
 * スキルマップ詳細情報
 */
class SkillMapDetail extends React.Component<IProps, IState> {
	protected materialDownloadForm: HTMLFormElement | null;
	protected fileIndexInput: HTMLInputElement | null;
	protected fileTypeInput: HTMLInputElement | null;
	protected cookieNameInput: HTMLInputElement | null;

	public constructor(props: IProps) {
		super(props);

		this.state = {
			...props,
		};

		// データ取得処理
		if (props.skillMapId) {
			this.props.fetchSkillMapDetail(props.skillMapId, props.token);
		}
	}

	/**
	 * Lifecycle Methods
	 */
	public componentDidMount() {
		window.scrollTo({
			top: 0,
		});
		if (!this.props.match || !this.props.match.params) {
			return;
		}

		const skillMapId = this.props.match.params.skill_map_id;

		// データ取得処理
		this.props.fetchSkillMapDetail(skillMapId, this.props.token);
	}

	/**
	 * Lifecycle Methods
	 */
	public componentWillUnmount() {
		// データを破棄
		this.props.resetSkillMapDetail();
	}

	/**
	 * HTMLテーブルの内容を作成する
	 * @param key TableRowのキー
	 * @param itemName 項目名
	 * @param itemValue 項目値
	 */
	public createTrElm(key: string, itemName?: string, itemValue?: string) {
		const classes = this.props.classes;

		return (
			<TableRow key={key}>
				<TableCell className={classes.cellItemName}>
					{itemName}
				</TableCell>
				<TableCell className={classes.cellItemValue}>
					{itemValue}
				</TableCell>
			</TableRow>
		);
	}

	/**
	 * スキル関連資料ダウンロード
	 * @param event クリックイベント
	 */
	public downloadFile(event: React.MouseEvent<HTMLElement>) {
		const fileIndex = event.currentTarget.getAttribute('data-fileindex');
		const fileType = event.currentTarget.getAttribute('data-filetype');
		if (this.fileIndexInput != null) {
			let searchCondition = this.fileIndexInput.value;
			const dto = JSON.parse(searchCondition);
			dto.fileIndex = fileIndex;
			searchCondition = JSON.stringify(dto);
			this.fileIndexInput.value = searchCondition;
			if (fileType && this.fileTypeInput) {
				this.fileTypeInput.value = fileType;
			}
			if (fileIndex && this.cookieNameInput) {
				this.cookieNameInput.value = 'TSQ_FD_' + fileIndex;
			}
		}

		if (this.materialDownloadForm != null) {
			this.materialDownloadForm.submit();
		}
	}

	/**
	 * スキル関連資料セルの内容を作成する
	 * @param skillInfo スキル情報
	 */
	public getMaterialFiles(materialFileList?: ISkillMapMaterialFile[]) {
		const classes = this.props.classes;
		const rows: JSX.Element[] = [];

		if (materialFileList && materialFileList.length > 0) {
			for (let i = 0; i < materialFileList.length; i++) {
				const materialFileDTO = materialFileList[i];
				const materialName = materialFileDTO.materialName;
				const fileName = materialFileDTO.fileName;
				const fileType = materialFileDTO.fileType;
				const fileIndex = materialFileDTO.fileIndex;
				const buttonId = 'buttonId' + i;
				const linkId = 'linkId' + i;
				const row = (
					<div key={'div' + i}>
						<span>{materialName}</span>
						<span
							id={linkId}
							className={classes.link + ' ' + classes.fileLink}
							data-fileindex={fileIndex}
							data-filetype={fileType}
							// tslint:disable-next-line: jsx-no-bind
							onClick={this.downloadFile.bind(this)}
						>
							{fileName} {fileType}
						</span>
						<CloudDownloadIcon
							id={buttonId}
							data-fileindex={fileIndex}
							data-filetype={fileType}
							// tslint:disable-next-line: jsx-no-bind
							onClick={this.downloadFile.bind(this)}
							className={classes.downloadButton}
						/>
					</div>
				);
				rows.push(row);
			}
		}

		return rows;
	}

	public render() {
		const { classes, skillMapInfo } = this.props;
		const { materialFileList } = skillMapInfo;

		return (
			<div className={classes.root}>
				<div className={classes.wrapDiv}>
					<Typography
						variant="h6"
						color="inherit"
						className={classes.flex}
					>
						{i18n.t('SkillMapDetail_0001')}
					</Typography>
					<Paper className={classes.paper}>
						<Table className={classes.table}>
							<TableBody>
								{this.createTrElm(
									'managementCode',
									i18n.t('SkillMapDetail_0002'),
									skillMapInfo.managementCode
								)}
								{this.createTrElm(
									'skillMapName',
									i18n.t('SkillMapDetail_0003'),
									skillMapInfo.skillMapName
								)}
								{this.createTrElm(
									'skillMapId',
									i18n.t('SkillMapDetail_0004'),
									skillMapInfo.skillMapId
								)}
								{this.createTrElm(
									'subType',
									i18n.t('SkillMapDetail_0005'),
									skillMapInfo.subType
								)}
								{this.createTrElm(
									'compulsoryID',
									i18n.t('SkillMapDetail_0006'),
									skillMapInfo.compulsoryID
								)}
								{this.createTrElm(
									'target',
									i18n.t('SkillMapDetail_0007'),
									skillMapInfo.target
								)}
								{this.createTrElm(
									'skillMapManager',
									i18n.t('SkillMapDetail_0008'),
									skillMapInfo.skillMapManager
								)}
							</TableBody>
						</Table>
					</Paper>
				</div>
				<div className={classes.wrapDiv}>
					<Typography
						variant="h6"
						color="inherit"
						className={classes.flex}
					>
						{i18n.t('SkillMapDetail_0009')}
					</Typography>
					<Paper className={classes.paper}>
						<Table className={classes.table}>
							<TableBody>
								{this.createTrElm(
									'purpose',
									i18n.t('SkillMapDetail_0010'),
									skillMapInfo.purpose
								)}
								{this.createTrElm(
									'outline',
									i18n.t('SkillMapDetail_0011'),
									skillMapInfo.outline
								)}
								{this.createTrElm(
									'applyFlg',
									i18n.t('SkillMapDetail_0012'),
									skillMapInfo.applyFlg === '0'
										? i18n.t('SkillMapDetail_0013')
										: skillMapInfo.applyFlg === '1'
										? i18n.t('SkillMapDetail_0014')
										: i18n.t('SkillMapDetail_0015')
								)}
								<TableRow key="materialFileList">
									<TableCell className={classes.cellItemName}>
										{i18n.t('SkillMapDetail_0016')}
									</TableCell>
									<TableCell
										className={classes.cellItemValue}
									>
										{this.getMaterialFiles(
											materialFileList
										)}
									</TableCell>
								</TableRow>
								{this.createTrElm(
									'comment',
									i18n.t('SkillMapDetail_0017'),
									skillMapInfo.comment
								)}
							</TableBody>
						</Table>
					</Paper>
				</div>
				<form
					method="POST"
					action={
						Config.tsqPath +
						'/tsq/pkg/action/downloadSkillMapMaterialFile.action'
					}
					ref={form => (this.materialDownloadForm = form)}
					target="_blank"
				>
					<input
						type="hidden"
						name="searchCondition"
						value={`{"skillMapId":"${
							skillMapInfo.skillMapId
						}","fileIndex":"0"}`}
						ref={fileIndexInput =>
							(this.fileIndexInput = fileIndexInput)
						}
					/>
					<input
						type="hidden"
						name="__tsqCookieName"
						value="TSQ_FD_0"
						ref={cookieName => (this.cookieNameInput = cookieName)}
					/>
					<input
						type="hidden"
						name="__tsqFileType"
						value="*"
						ref={fileType => (this.fileTypeInput = fileType)}
					/>
				</form>
			</div>
		);
	}
}

const mapStateToProps = (state: IRootState) => ({
	skillMapInfo: state.skillMapDetail.skillMapInfo,
	token: state.session.token,
	userAttr: state.session.userAttr,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	fetchSkillMapDetail: (skillMapId: string, token: string) => {
		const scenario = ScenarioFactory.createFetchSkillMapDetail(
			dispatch,
			token
		);
		scenario.run(skillMapId);
	},
	resetSkillMapDetail: () => {
		dispatch(
			setSkillMapDetail({
				skillMapInfo: {
					skillMapId: '',
				},
			})
		);
	},
});

const mergeProps = (
	state: IStateProps,
	events: IDispatchProps,
	ownProps: IOwnProps
) => ({
	...state,
	...events,
	...ownProps,
});

type IStateProps = ReturnType<typeof mapStateToProps>;
type IDispatchProps = ReturnType<typeof mapDispatchToProps>;
type IOwnProps = WithStyles<typeof styles> &
	RouteComponentProps<IRouteParams, StaticContext, IRouteState> &
	ISkillMapDetailProps;
type IProps = ReturnType<typeof mergeProps>;

const Connected = connect(
	mapStateToProps,
	mapDispatchToProps,
	mergeProps
)(SkillMapDetail);

const Styled = withStyles(styles)(Connected);

const Export: React.ComponentType<ISkillMapDetailProps> = Styled;

export default Export;



// WEBPACK FOOTER //
// ./src/containers/SkillMapDetail.tsx